import { useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Cpu, FlaskConical, Layers, Plus, Stethoscope, Trash2, FileSearch } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { date } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, Dialogue, EntetePage, EtatErreur, EtatVide, FilContexte, LienBouton, Onglets, Panneau,
  Valeur, useConfirmation, useNotifier,
} from '../../components/ui.tsx';
import { BadgeStatutDossier, BadgeStatutIntervention, DOMAINES_SYSTEME, PROTOCOLES } from '../../components/statuts.tsx';
import { CoupeBatiment } from './CoupeBatiment.tsx';
import { FormulaireEquipement, cheminLieu, type Lieu, type Systeme } from './FormulaireEquipement.tsx';
import { USAGES } from './PageSites.tsx';
import { OngletCoffre } from './OngletCoffre.tsx';
import { BoutonHorsLigne } from '../../components/BoutonHorsLigne.tsx';
import { CarnetSite } from '../../components/Carnet.tsx';

interface Equip { id: string; label: string; category: string | null; manufacturer: string | null; range_name: string | null; reference: string | null;
  hw_version: string | null; sw_version: string | null; firmware: string | null; protocols: string[]; location_id: string | null; system_id: string | null; network_address: string | null }
interface Site {
  id: string; name: string; address: string | null; city: string | null; usage: string | null; notes: string | null; demo: boolean;
  client: { id: string; name: string } | null; lieux: Lieu[]; systemes: Systeme[]; equipements: Equip[];
  /** Archivés : masqués de la liste, fiche et carnet consultables. */
  equipements_archives: { id: string; label: string; reference: string | null; archived_at: string; archive_reason: string; replaced_by_id: string | null; remplace_par: string | null }[];
  interventions: { id: string; reference: string; title: string; status: string; created_at: string }[];
  dossiers: { id: string; reference: string; title: string; status: string; created_at: string; closed_at: string | null }[];
}

type Onglet = 'equipements' | 'carnet' | 'batiment' | 'systemes' | 'historique' | 'coffre';

export function PageSite() {
  const { id } = useParams();
  const { peut } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const notifier = useNotifier();
  const { confirmer, element: confirmation } = useConfirmation();
  const q = useQuery({ queryKey: ['site', id], queryFn: () => api.get<Site>(`/api/sites/${id}`) });
  // Lien direct vers un onglet (?onglet=carnet), par exemple depuis un carnet d'équipement.
  const [params] = useSearchParams();
  const ongletInitial = params.get('onglet');
  const [onglet, setOnglet] = useState<Onglet>(ongletInitial === 'carnet' || ongletInitial === 'historique' ? ongletInitial : 'equipements');
  const [selection, setSelection] = useState<string | null>(null);
  const [formEquip, setFormEquip] = useState(false);
  const [formLieu, setFormLieu] = useState<null | { kind: Lieu['kind']; parentId: string | null }>(null);
  const [hauteurLieu, setHauteurLieu] = useState<Lieu | null>(null);
  const [formSysteme, setFormSysteme] = useState(false);

  const s = q.data;
  const descendants = useMemo(() => {
    const m = new Map<string, Set<string>>();
    if (!s) return m;
    for (const l of s.lieux) {
      const set = new Set<string>([l.id]);
      let ajoute = true;
      while (ajoute) {
        ajoute = false;
        for (const x of s.lieux) if (x.parent_id && set.has(x.parent_id) && !set.has(x.id)) { set.add(x.id); ajoute = true; }
      }
      m.set(l.id, set);
    }
    return m;
  }, [s]);
  const compte = (lieuId: string) => s?.equipements.filter((e) => e.location_id && descendants.get(lieuId)?.has(e.location_id)).length ?? 0;
  const equipementsFiltres = (s?.equipements ?? []).filter((e) => !selection || (e.location_id && descendants.get(selection)?.has(e.location_id)));

  if (q.isLoading) return <Chargement lignes={6} />;
  if (q.isError || !s) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;

  const batiments = s.lieux.filter((l) => l.kind === 'batiment');
  const recharger = () => qc.invalidateQueries({ queryKey: ['site', id] });

  const supprimerLieu = async (l: Lieu) => {
    if (!(await confirmer(`Supprimer « ${l.name} » ?`, 'Les étages et zones qu’il contient seront aussi supprimés. Les équipements doivent d’abord être déplacés.', 'Supprimer'))) return;
    try { await api.del(`/api/sites/${s.id}/lieux/${l.id}`); notifier('Lieu supprimé.'); recharger(); }
    catch (e) { notifier(messageErreur(e), true); }
  };

  return (
    <div className="pile-l">
      <EntetePage
        fil={<FilContexte noeuds={[...(s.client ? [{ type: 'Client', libelle: s.client.name }] : []), { type: 'Site', libelle: s.name }]} />}
        titre={<span className="rangee" style={{ gap: 10 }}>{s.name}{s.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démonstration — données fictives</Badge>}</span>}
        sousTitre={[s.address, s.city, s.usage && USAGES[s.usage]].filter(Boolean).join(' — ') || undefined}
        actions={<>
          {peut('intervention.ecrire') && <LienBouton to={`/interventions/nouvelle?site=${s.id}`} variante="principal" icone={<Stethoscope aria-hidden />}>Diagnostiquer</LienBouton>}
          {peut('dossier.ecrire') && <LienBouton to={`/etudes/nouveau?site=${s.id}`} icone={<FileSearch aria-hidden />}>Nouveau dossier</LienBouton>}
          <BoutonHorsLigne cle={`site:${s.id}`} type="site" libelle={s.name} urls={[`/api/sites/${s.id}`, `/api/sites/${s.id}/carnet`, ...s.equipements.map((x) => `/api/equipements/${x.id}`)]} />
        </>} />

      {s.notes && <Alerte ton="info" titre="Consignes du site">{s.notes}</Alerte>}

      <Onglets libelle="Sections du site" actif={onglet} changer={setOnglet} onglets={[
        { id: 'equipements', libelle: 'Équipements', compteur: s.equipements.length },
        { id: 'carnet', libelle: 'Carnet du site' },
        { id: 'batiment', libelle: 'Bâtiments et zones', compteur: s.lieux.length },
        { id: 'systemes', libelle: 'Systèmes', compteur: s.systemes.length },
        { id: 'historique', libelle: 'Historique', compteur: s.interventions.length + s.dossiers.length },
        ...(peut('secret.lire') ? [{ id: 'coffre' as const, libelle: 'Coffre' }] : []),
      ]} />

      {onglet === 'equipements' && (
        <div className={batiments.length ? 'site-disposition' : ''}>
          {batiments.length > 0 && (
            <div className="pile">
              {batiments.map((b) => <CoupeBatiment key={b.id} batiment={b} lieux={s.lieux} compte={compte} selection={selection} choisir={setSelection} />)}
            </div>
          )}
          <Panneau serre titre={selection ? `Équipements — ${cheminLieu(s.lieux, selection)}` : 'Tous les équipements'}
            actions={<>
              {selection && <Bouton compact variante="fantome" onClick={() => setSelection(null)}>Tout afficher</Bouton>}
              {peut('site.ecrire') && <Bouton compact variante="principal" icone={<Plus aria-hidden />} onClick={() => setFormEquip(true)}>Ajouter</Bouton>}
            </>}>
            {equipementsFiltres.length === 0
              ? <EtatVide icone={<Cpu />} titre={selection ? 'Aucun équipement à cet emplacement' : 'Aucun équipement'}
                  texte="Ajoutez les automates, passerelles, compteurs et appareils installés, avec leur référence exacte : c’est elle qui relie l’équipement à sa documentation." />
              : (
                <div className="tableau-conteneur">
                  <table className="tableau">
                    <thead><tr><th>Désignation</th><th>Fabricant / gamme</th><th>Référence</th><th className="expert-seulement">Versions</th><th className="expert-seulement">Protocoles</th><th className="masque-etroit">Emplacement</th></tr></thead>
                    <tbody>
                      {equipementsFiltres.map((e) => (
                        <tr key={e.id} className="ligne-lien" onClick={() => nav(`/sites/equipements/${e.id}`)}>
                          <td><Link to={`/sites/equipements/${e.id}`} onClick={(ev) => ev.stopPropagation()}><strong>{e.label}</strong></Link>
                            {e.category && <div className="tres-petit discret">{e.category}</div>}</td>
                          <td><Valeur v={[e.manufacturer, e.range_name].filter(Boolean).join(' — ')} /></td>
                          <td><Valeur v={e.reference} mono /></td>
                          <td className="expert-seulement tres-petit"><Valeur v={[e.hw_version && `mat. ${e.hw_version}`, e.sw_version && `log. ${e.sw_version}`, e.firmware && `fw ${e.firmware}`].filter(Boolean).join(', ')} /></td>
                          <td className="expert-seulement tres-petit"><Valeur v={e.protocols} /></td>
                          <td className="masque-etroit tres-petit">{e.location_id ? cheminLieu(s.lieux, e.location_id) : <span className="inconnu">Non précisé</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            {s.equipements_archives.length > 0 && !selection && (
              <details className="site-archives">
                <summary className="petit"><Archive size={16} aria-hidden /> {s.equipements_archives.length} équipement{s.equipements_archives.length > 1 ? 's' : ''} archivé{s.equipements_archives.length > 1 ? 's' : ''}</summary>
                <div className="liste">{s.equipements_archives.map((x) => (
                  <Link key={x.id} to={`/sites/equipements/${x.id}`} className="liste-element"><span className="liste-element-corps">
                    <span className="liste-element-titre">{x.label}{x.reference ? <span className="mono petit discret"> {x.reference}</span> : null}</span>
                    <span className="petit discret">Archivé le {date(x.archived_at)} — {x.archive_reason}{x.remplace_par ? ` — remplacé par ${x.remplace_par}` : ''}</span></span></Link>))}
                </div>
              </details>
            )}
          </Panneau>
        </div>
      )}

      {onglet === 'carnet' && (
        <CarnetSite siteId={s.id} filtres={{
          lieux: s.lieux.map((l) => ({ id: l.id, libelle: cheminLieu(s.lieux, l.id) })),
          systemes: s.systemes,
          equipements: [...s.equipements.map((x) => ({ id: x.id, label: x.label })), ...s.equipements_archives.map((x) => ({ id: x.id, label: x.label, archive: true }))],
        }} />
      )}

      {onglet === 'batiment' && (
        <Panneau titre="Bâtiments, étages et zones" actions={peut('site.ecrire') && <Bouton compact icone={<Plus aria-hidden />} onClick={() => setFormLieu({ kind: 'batiment', parentId: null })}>Bâtiment</Bouton>}>
          {batiments.length === 0
            ? <EtatVide icone={<Layers />} titre="Aucun bâtiment" texte="Décrivez la structure : bâtiment, puis étages, puis zones. Les équipements s’y rattachent." />
            : <ArbreLieux lieux={s.lieux} parent={null} compte={compte} peutEcrire={peut('site.ecrire')} ajouter={setFormLieu} supprimer={supprimerLieu} hauteur={setHauteurLieu} />}
        </Panneau>
      )}

      {onglet === 'systemes' && (
        <Panneau serre titre="Systèmes techniques" actions={peut('site.ecrire') && <Bouton compact icone={<Plus aria-hidden />} onClick={() => setFormSysteme(true)}>Système</Bouton>}>
          {s.systemes.length === 0
            ? <EtatVide titre="Aucun système" texte="Un système regroupe les équipements d’une fonction : éclairage, chauffage, ventilation, comptage, supervision…" />
            : <div className="liste">{s.systemes.map((sy) => (
              <div key={sy.id} className="liste-element">
                <span className="liste-element-corps"><span className="liste-element-titre">{sy.name}</span>
                  <span className="petit discret">{DOMAINES_SYSTEME[sy.domain]}{sy.protocols.length ? ` — ${sy.protocols.join(', ')}` : ''}</span></span>
                <span className="petit secondaire">{s.equipements.filter((e) => e.system_id === sy.id).length} équip.</span>
              </div>))}</div>}
        </Panneau>
      )}

      {onglet === 'historique' && (
        <div className="grille-2">
          <Panneau serre titre="Interventions">
            {s.interventions.length === 0 ? <EtatVide titre="Aucune intervention" /> : <div className="liste">{s.interventions.map((i) => (
              <Link key={i.id} to={`/interventions/${i.id}`} className="liste-element"><span className="liste-element-corps">
                <span className="liste-element-titre">{i.title}</span><span className="petit discret"><span className="mono">{i.reference}</span> — {date(i.created_at)}</span></span>
                <BadgeStatutIntervention statut={i.status} /></Link>))}</div>}
          </Panneau>
          <Panneau serre titre="Dossiers d’étude">
            {s.dossiers.length === 0 ? <EtatVide titre="Aucun dossier" /> : <div className="liste">{s.dossiers.map((x) => (
              <Link key={x.id} to={`/etudes/${x.id}`} className="liste-element"><span className="liste-element-corps">
                <span className="liste-element-titre">{x.title}</span><span className="petit discret"><span className="mono">{x.reference}</span> — {date(x.created_at)}</span></span>
                {x.closed_at ? <Badge ton="neutre" icone={<Archive size={14} aria-hidden />}>Classé sans suite</Badge> : <BadgeStatutDossier statut={x.status} />}</Link>))}</div>}
          </Panneau>
        </div>
      )}

      {onglet === 'coffre' && <OngletCoffre siteId={s.id} equipements={s.equipements} />}

      {formEquip && <FormulaireEquipement ouvert fermer={() => setFormEquip(false)} siteId={s.id} lieux={s.lieux} systemes={s.systemes}
        initial={selection ? { label: '', category: null, manufacturer: null, range_name: null, reference: null, hw_version: null, sw_version: null, firmware: null,
          protocols: [], network_address: null, location_id: selection, system_id: null } : undefined}
        enregistre={(eid) => { setFormEquip(false); notifier('Équipement ajouté.'); recharger(); nav(`/sites/equipements/${eid}`); }} />}
      {formLieu && <FormulaireLieu siteId={s.id} {...formLieu} parentNom={formLieu.parentId ? cheminLieu(s.lieux, formLieu.parentId) : null}
        fermer={() => setFormLieu(null)} enregistre={() => { setFormLieu(null); recharger(); }} />}
      {hauteurLieu && <DialogueHauteurEtage siteId={s.id} lieu={hauteurLieu} fermer={() => setHauteurLieu(null)} enregistre={() => { setHauteurLieu(null); recharger(); }} />}
      {formSysteme && <FormulaireSysteme siteId={s.id} fermer={() => setFormSysteme(false)} enregistre={() => { setFormSysteme(false); recharger(); }} />}
      {confirmation}
    </div>
  );
}

function ArbreLieux({ lieux, parent, compte, peutEcrire, ajouter, supprimer, hauteur }: {
  lieux: Lieu[]; parent: string | null; compte: (id: string) => number; peutEcrire: boolean;
  ajouter: (v: { kind: Lieu['kind']; parentId: string | null }) => void; supprimer: (l: Lieu) => void;
  hauteur: (l: Lieu) => void;
}) {
  const enfants = lieux.filter((l) => l.parent_id === parent).sort((a, b) => (b.level ?? 0) - (a.level ?? 0) || a.name.localeCompare(b.name));
  if (!enfants.length) return null;
  const LIB = { batiment: 'Bâtiment', etage: 'Étage', zone: 'Zone' };
  return (
    <ul className="arbre">
      {enfants.map((l) => (
        <li key={l.id}>
          <div className="arbre-noeud">
            <span className="arbre-type tres-petit">{LIB[l.kind]}</span>
            <strong>{l.name}</strong>
            <span className="tres-petit discret">{compte(l.id)} équip.</span>
            {l.kind === 'etage' && <span className="tres-petit discret">{l.floor_height_m ? `${l.floor_height_m} m · ${l.floor_height_source}` : 'hauteur d’étage à saisir'}</span>}
            {peutEcrire && (
              <span className="groupe-btn" style={{ marginLeft: 'auto' }}>
                {l.kind === 'etage' && <Bouton compact variante="fantome" onClick={() => hauteur(l)} aria-label={`Hauteur de ${l.name}`}>Hauteur d’étage</Bouton>}
                {l.kind === 'batiment' && <Bouton compact variante="fantome" icone={<Plus aria-hidden />} onClick={() => ajouter({ kind: 'etage', parentId: l.id })}>Étage</Bouton>}
                {l.kind !== 'zone' && <Bouton compact variante="fantome" icone={<Plus aria-hidden />} onClick={() => ajouter({ kind: 'zone', parentId: l.id })}>Zone</Bouton>}
                <Bouton compact variante="fantome" aria-label={`Supprimer ${l.name}`} icone={<Trash2 aria-hidden />} onClick={() => supprimer(l)} />
              </span>
            )}
          </div>
          <ArbreLieux lieux={lieux} parent={l.id} compte={compte} peutEcrire={peutEcrire} ajouter={ajouter} supprimer={supprimer} hauteur={hauteur} />
        </li>
      ))}
    </ul>
  );
}

function FormulaireLieu({ siteId, kind, parentId, parentNom, fermer, enregistre }: {
  siteId: string; kind: Lieu['kind']; parentId: string | null; parentNom: string | null; fermer: () => void; enregistre: () => void;
}) {
  const [nom, setNom] = useState('');
  const [niveau, setNiveau] = useState('');
  const [hauteur, setHauteur] = useState(''), [source, setSource] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const LIB = { batiment: 'un bâtiment', etage: 'un étage', zone: 'une zone' };
  const valider = async () => {
    if (!nom.trim()) { setErreur('Indiquez un nom.'); return; }
    setEnvoi(true);
    try {
      await api.post(`/api/sites/${siteId}/lieux`, { kind, parentId, name: nom, level: kind === 'etage' && niveau !== '' ? Number(niveau) : null,
        ...(kind === 'etage' && hauteur.trim() ? { hauteurM: Number(hauteur.replace(',', '.')), sourceHauteur: source.trim() || null } : {}) });
      enregistre();
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre={`Ajouter ${LIB[kind]}`} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={valider}>Ajouter</Bouton></>}>
      <form className="pile" onSubmit={(e) => { e.preventDefault(); valider(); }}>
        {parentNom && <p className="petit discret">Dans : {parentNom}</p>}
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Champ libelle="Nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder={kind === 'etage' ? 'ex. R+1, Sous-sol' : kind === 'zone' ? 'ex. Bureaux nord, Local TGBT' : 'ex. Bâtiment A'} />
        {kind === 'etage' && <Champ libelle="Niveau (nombre)" type="number" inputMode="numeric" value={niveau} onChange={(e) => setNiveau(e.target.value)} aide="0 pour le rez-de-chaussée, -1 pour le sous-sol. Sert à empiler la coupe." />}
        {kind === 'etage' && <><Champ libelle="Hauteur vers l’étage supérieur (m, facultative)" inputMode="decimal" value={hauteur} onChange={(e) => setHauteur(e.target.value)} placeholder="hauteur d’étage à saisir" />
          <Champ libelle="Source de la hauteur d’étage" value={source} onChange={(e) => setSource(e.target.value)} placeholder="Cote du plan, relevé de l’entreprise…" /></>}
      </form>
    </Dialogue>
  );
}

function DialogueHauteurEtage({ siteId, lieu, fermer, enregistre }: { siteId: string; lieu: Lieu; fermer: () => void; enregistre: () => void }) {
  const [hauteur, setHauteur] = useState(lieu.floor_height_m ?? ''), [source, setSource] = useState(lieu.floor_height_source ?? '');
  const [erreur, setErreur] = useState(''), [envoi, setEnvoi] = useState(false);
  const valider = async () => {
    setEnvoi(true); setErreur('');
    try { await api.put(`/api/sites/${siteId}/lieux/${lieu.id}/hauteur`, { hauteurM: hauteur.trim() || null, source: source.trim() || null }); enregistre(); }
    catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return <Dialogue titre="Hauteur d’étage" ouvert fermer={fermer} pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} disabled={!!hauteur.trim() && !source.trim()} onClick={valider}>Enregistrer la hauteur</Bouton></>}>
    <div className="pile"><p>{lieu.name} · du sol de cet étage au sol du niveau immédiatement supérieur. Toute modification impose de revalider les métrés concernés.</p>
      {erreur && <Alerte ton="danger">{erreur}</Alerte>}
      <Champ libelle="Hauteur vers l’étage supérieur (m)" value={hauteur} inputMode="decimal" onChange={(e) => setHauteur(e.target.value)} placeholder="hauteur d’étage à saisir" />
      <Champ libelle="Source de la hauteur d’étage" value={source} onChange={(e) => setSource(e.target.value)} placeholder="À saisir" />
    </div>
  </Dialogue>;
}

function FormulaireSysteme({ siteId, fermer, enregistre }: { siteId: string; fermer: () => void; enregistre: () => void }) {
  const [nom, setNom] = useState('');
  const [domaine, setDomaine] = useState('supervision');
  const [protocoles, setProtocoles] = useState<string[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const valider = async () => {
    if (!nom.trim()) { setErreur('Indiquez un nom.'); return; }
    setEnvoi(true);
    try { await api.post(`/api/sites/${siteId}/systemes`, { name: nom, domain: domaine, protocols: protocoles }); enregistre(); }
    catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Ajouter un système" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={valider}>Ajouter</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Champ libelle="Nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="ex. GTB CVC bâtiment A" />
        <ChampChoix libelle="Domaine" value={domaine} onChange={(e) => setDomaine(e.target.value)} options={Object.entries(DOMAINES_SYSTEME).map(([valeur, libelle]) => ({ valeur, libelle }))} />
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="champ-libelle" style={{ marginBottom: 6 }}>Protocoles</legend>
          <div className="rangee" style={{ gap: 6 }}>{PROTOCOLES.map((p) => (
            <button key={p} type="button" aria-pressed={protocoles.includes(p)} className={`btn btn-compact ${protocoles.includes(p) ? 'btn-principal' : ''}`}
              onClick={() => setProtocoles(protocoles.includes(p) ? protocoles.filter((x) => x !== p) : [...protocoles, p])}>{p}</button>))}</div>
        </fieldset>
      </div>
    </Dialogue>
  );
}
